"use client";

import { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import Reveal from "../ui/Reveal";
import Icon from "../ui/Icon";
import Modal from "../ui/Modal";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.kennybruce.cosebu";
const MARKETPLACE_WEB = "https://marketplace.cosebu.com";
const MANAGEMENT_URL = "https://management.cosebu.com";

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
const isValidPhone = (value) => /^\+?\d[\d\s-]{6,}$/.test(value.trim());

const EarlyAccessForm = ({ t, onDone }) => {
  const [form, setForm] = useState({ fullName: "", whatsapp: "", email: "" });
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error

  const errors = {
    fullName: form.fullName.trim().length >= 3 ? "" : t.downloads.modal.errorName,
    whatsapp: isValidPhone(form.whatsapp) ? "" : t.downloads.modal.errorWhatsapp,
    email: isValidEmail(form.email) ? "" : t.downloads.modal.errorEmail,
  };
  const isValid = !errors.fullName && !errors.whatsapp && !errors.email;

  const change = (field) => (event) => {
    const { value } = event.target;
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const blur = (field) => () =>
    setTouched((previous) => ({ ...previous, [field]: true }));

  const submit = (event) => {
    event.preventDefault();
    setTouched({ fullName: true, whatsapp: true, email: true });
    if (!isValid || status === "submitting") return;

    setStatus("submitting");
    // No public endpoint yet — the request is queued for the team to collect.
    window.setTimeout(() => setStatus("done"), 1200);
  };

  if (status === "done") {
    return (
      <div className="py-4 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-line bg-positive-soft text-positive">
          <Icon name="check" size={22} strokeWidth={2} />
        </span>
        <h3 className="title-sm mt-5">{t.downloads.modal.successTitle}</h3>
        <p className="subhead mx-auto mt-3 max-w-[42ch] text-[15px]">
          {t.downloads.modal.successMessage}
        </p>
        <button type="button" onClick={onDone} className="btn btn--secondary mt-7">
          {t.ui.close}
        </button>
      </div>
    );
  }

  const fields = [
    {
      name: "fullName",
      label: t.downloads.modal.fullName,
      placeholder: t.downloads.modal.fullNamePlaceholder,
      type: "text",
      autoComplete: "name",
    },
    {
      name: "whatsapp",
      label: t.downloads.modal.whatsapp,
      placeholder: t.downloads.modal.whatsappPlaceholder,
      type: "tel",
      autoComplete: "tel",
    },
    {
      name: "email",
      label: t.downloads.modal.email,
      placeholder: t.downloads.modal.emailPlaceholder,
      type: "email",
      autoComplete: "email",
    },
  ];

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      <p className="meta">{t.downloads.modal.apology}</p>

      {fields.map((field) => {
        const invalid = Boolean(touched[field.name] && errors[field.name]);
        return (
          <div key={field.name} className="field">
            <label className="field__label" htmlFor={`ea-${field.name}`}>
              {field.label}
            </label>
            <input
              id={`ea-${field.name}`}
              className="input"
              type={field.type}
              value={form[field.name]}
              onChange={change(field.name)}
              onBlur={blur(field.name)}
              placeholder={field.placeholder}
              autoComplete={field.autoComplete}
              aria-invalid={invalid}
              aria-describedby={invalid ? `ea-${field.name}-error` : undefined}
              required
            />
            {invalid ? (
              <p className="field__error" id={`ea-${field.name}-error`} role="alert">
                <Icon name="close" size={13} strokeWidth={2} />
                {errors[field.name]}
              </p>
            ) : null}
          </div>
        );
      })}

      <p className="field__hint">{t.downloads.modal.privacy}</p>

      <div className="flex flex-col gap-3 border-t border-line pt-5 sm:flex-row-reverse">
        <button
          type="submit"
          className="btn btn--primary sm:flex-1"
          disabled={status === "submitting"}
        >
          {status === "submitting" ? (
            <>
              <span
                className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-hidden="true"
              />
              {t.downloads.modal.submitting}
            </>
          ) : (
            t.downloads.modal.submit
          )}
        </button>
        <button type="button" onClick={onDone} className="btn btn--ghost">
          {t.downloads.modal.cancel}
        </button>
      </div>
    </form>
  );
};

const Downloads = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* ---------- header --------------------------------------------- */}
      <section className="band band--flush pt-14 md:pt-20">
        <div className="shell">
          <Reveal>
            <span className="eyebrow">{t.downloads.eyebrow}</span>
          </Reveal>
          <Reveal delay={0.04}>
            <h1 className="display mt-5 max-w-[14ch]">{t.downloads.title}</h1>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="subhead mt-6 max-w-prose">{t.downloads.subtitle}</p>
          </Reveal>
        </div>
      </section>

      {/* ---------- the two ways in -------------------------------------- */}
      <section className="band">
        <div className="shell grid gap-6 lg:grid-cols-2">
          <Reveal className="panel flex flex-col p-7 md:p-9">
            <div className="flex items-center justify-between gap-4">
              <span className="eyebrow">{t.downloads.forShoppers}</span>
              <Icon name="cart" size={20} className="text-ink-3" />
            </div>

            <h2 className="title-sm mt-6">{t.downloads.marketplace.title}</h2>
            <p className="subhead mt-4 text-[15px]">
              {t.downloads.marketplace.description}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--primary sm:flex-1"
              >
                <Icon name="google" size={16} />
                {t.downloads.marketplace.androidCta}
              </a>
              <a
                href={MARKETPLACE_WEB}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--secondary sm:flex-1"
              >
                <Icon name="globe" size={16} />
                {t.downloads.webApp}
              </a>
            </div>

            <div className="mt-auto flex flex-col gap-3 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between">
              <p className="meta max-w-[38ch] text-[13px]">{t.downloads.iosNote}</p>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="btn btn--secondary btn--sm flex-none"
              >
                <Icon name="apple" size={15} />
                {t.downloads.earlyAccessCta}
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.06} className="panel flex flex-col p-7 md:p-9">
            <div className="flex items-center justify-between gap-4">
              <span className="eyebrow">{t.downloads.forBusinesses}</span>
              <Icon name="store" size={20} className="text-ink-3" />
            </div>

            <h2 className="title-sm mt-6">{t.downloads.management.title}</h2>
            <p className="subhead mt-4 text-[15px]">
              {t.downloads.management.description}
            </p>

            <ul className="mt-8 divide-rule border-t border-line">
              {t.management.features.slice(0, 4).map((feature) => (
                <li key={feature} className="flex items-center gap-3 py-3">
                  <Icon
                    name="check"
                    size={15}
                    strokeWidth={2}
                    className="flex-none text-accent"
                  />
                  <span className="text-[14px] text-ink-2">{feature}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-8">
              <a
                href={MANAGEMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--primary btn--block sm:w-auto"
              >
                {t.downloads.management.cta}
                <Icon name="arrowUpRight" size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- help ------------------------------------------------- */}
      <section className="band">
        <div className="shell">
          <Reveal className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="title-sm">{t.downloads.helpTitle}</h2>
              <p className="subhead mt-3 max-w-prose text-[15px]">
                {t.downloads.helpText}
              </p>
            </div>
            <div className="flex flex-none flex-wrap gap-3">
              <a href={`mailto:${t.footer.email}`} className="btn btn--secondary">
                <Icon name="mail" size={16} />
                {t.footer.email}
              </a>
              <a
                href={`https://wa.me/${t.about.whatsapp.replace(/[^\d]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--secondary"
              >
                <Icon name="whatsapp" size={16} />
                WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={t.downloads.modal.title}
        closeLabel={t.ui.close}
      >
        <EarlyAccessForm t={t} onDone={() => setOpen(false)} />
      </Modal>
    </>
  );
};

export default Downloads;
